import { lazy, Suspense } from "react";
import {
  Route,
  Routes,
  Link as RouterLink,
  useLocation,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

import { useActiveFarmContext } from "@/contexts";
import PageLoading from "@/pages/loading";

// Lazy load all page components for code splitting
const PageHome = lazy(() => import("@/pages/home"));
// const PageManageFields = lazy(() => import("@/pages/manageFields"));
const PageManageFieldsV2 = lazy(() => import("@/pages/manageFieldsV2"));
const PageFarms = lazy(() => import("@/pages/farms"));
const PageFieldsAnalyse = lazy(() => import("@/pages/fieldsAnalyse"));
const PageCirculations = lazy(() => import("@/pages/circulations"));
const PageScan = lazy(() => import("@/pages/scan"));
const PageNotFound = lazy(() => import("@/pages/notFound"));
const PageAcceptInvitation = lazy(() => import("@/pages/acceptInvitation"));
const PageInvitations = lazy(() => import("@/pages/invitations"));

const formatCrumb = (segment) => {
  return segment.charAt(0).toUpperCase() + segment.slice(1);
};

const BreadcrumbSX = {
  color: "#424242",
  fontSize: "0.875rem",
  fontWeight: 500,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  "&:hover": {
    color: "#000",
  },
};

const RoutesBox = ({ children }) => (
  <Box className="routes-box" sx={{ width: "100%", p: 2 }}>
    <MyBreadCrumbs location={location} />
    <Suspense fallback={<PageLoading />}>{children}</Suspense>
  </Box>
);

const MyBreadCrumbs = ({ location }) => {
  const pathSegments = location.pathname.split("/").filter(Boolean);

  // Don't show breadcrumbs on home page
  if (pathSegments.length === 0) return null;

  return (
    <Box sx={{ pb: 1 }}>
      <Breadcrumbs
        aria-label="breadcrumb"
        separator="›"
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: "#666",
            mx: 1,
          },
        }}
      >
        <Link component={RouterLink} to="/" underline="hover" sx={BreadcrumbSX}>
          Home
        </Link>

        {pathSegments.slice(0, -1).map((segment, index) => {
          const path = `/${pathSegments.slice(0, index + 1).join("/")}`;
          return (
            <Link
              key={path}
              component={RouterLink}
              to={path}
              underline="hover"
              sx={BreadcrumbSX}
            >
              {formatCrumb(segment)}
            </Link>
          );
        })}

        <Typography
          sx={{
            color: "#757575",
            fontSize: "0.875rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          {formatCrumb(pathSegments[pathSegments.length - 1])}
        </Typography>
      </Breadcrumbs>
    </Box>
  );
};

// ErrorBoundary Test Component
// const TestErrorPage = () => { throw new Error("This is a test error to demonstrate the error boundary!"); };

const MyRoutes = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const { activeFarm } = useActiveFarmContext();

  if (!isAuthenticated && location?.pathname === "/accept-invitation") {
    return (
      <RoutesBox>
        <Routes>
          <Route path="/accept-invitation" element={<PageAcceptInvitation />} />
          <Route path="*" element={<PageHome />} />
        </Routes>
      </RoutesBox>
    );
  }

  if (!isAuthenticated) {
    return (
      <RoutesBox>
        <PageHome />
      </RoutesBox>
    );
  }
  if (!activeFarm && location?.pathname !== "/accept-invitation" && location?.pathname !== "/invitations") {
    return (
      <RoutesBox>
        <PageFarms />
      </RoutesBox>
    );
  }
  return (
    <RoutesBox>
      <Routes>
        <Route path="/" element={<PageHome />} />
        <Route path="/accept-invitation" element={<PageAcceptInvitation />} />
        <Route path="/farms" element={<PageFarms />} />
        <Route path="/invitations" element={<PageInvitations />} />
        <Route path="/farms/analysis" element={<PageFieldsAnalyse />} />
        {/* <Route path="/farms/manage" element={<PageManageFields />} /> */}
        <Route path="/farms/manage" element={<PageManageFieldsV2 />} />
        <Route path="/circulations" element={<PageCirculations />} />
        <Route path="/scan" element={<PageScan />} />
        {/* <Route path="/crop-forecast" element={<PageCropForecast />} /> TODO: regional forecast page Paul - Ibrahim */}
        {/* <Route path="/test-error" element={<TestErrorPage />} /> TEST ROUTE. Uncomment and navigate to test ErrorBoundary catcher */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </RoutesBox>
  );
};

export default MyRoutes;
